// =====================================================
// FuMind_GM - Generic Notification Service
// Phase 2: One generic HR_Notifications list + one unified flow
// Flow: F_HR_Notifications_Unified
// Actions: create, load, unreadCount, markRead, markAllRead
// =====================================================

// TODO: Paste HTTP POST URL from F_HR_Notifications_Unified
const HR_NOTIFICATION_FLOW_URL = "/tracka-flow/v1/HR_NOTIFICATION";
const API_KEY = "";

export const DEFAULT_APPROVER_NOTIFICATION_RECIPIENTS = [
  { employeeCode: "HR001", role: "HRAdmin", email: "" },
];

async function parseJsonResponse(response) {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return { message: text, rawText: text };
  }
}

function normalizePowerAutomateResponse(rawData) {
  return rawData?.body || rawData || {};
}

function ensureNotificationFlowUrl() {
  if (!HR_NOTIFICATION_FLOW_URL || HR_NOTIFICATION_FLOW_URL.includes("PASTE_")) {
    throw new Error("F_HR_Notifications_Unified flow URL is not configured in notificationService.js.");
  }
}

function safeJsonParse(value, fallback = value) {
  if (typeof value !== "string") return fallback;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

function getValue(record = {}, ...names) {
  if (!record || typeof record !== "object") return "";
  for (const name of names) {
    if (record?.[name] !== undefined && record?.[name] !== null) return record[name];
  }
  const normalized = Object.keys(record).reduce((acc, key) => {
    acc[String(key).toLowerCase().replace(/[^a-z0-9]/g, "")] = record[key];
    return acc;
  }, {});
  for (const name of names) {
    const key = String(name).toLowerCase().replace(/[^a-z0-9]/g, "");
    if (normalized[key] !== undefined && normalized[key] !== null) return normalized[key];
  }
  return "";
}

function extractChoiceValue(value) {
  if (value === undefined || value === null) return "";
  if (["string", "number", "boolean"].includes(typeof value)) return String(value);
  if (Array.isArray(value)) return value.map(extractChoiceValue).filter(Boolean).join("; ");
  if (typeof value === "object") return extractChoiceValue(value.Value || value.value || value.Title || value.Email || value.DisplayName || "");
  return String(value);
}

function toBoolean(value, fallback = false) {
  if (value === undefined || value === null || value === "") return fallback;
  if (value === true || value === "true" || value === "True" || value === 1 || value === "1" || value === "Yes") return true;
  if (value === false || value === "false" || value === "False" || value === 0 || value === "0" || value === "No") return false;
  return fallback;
}

function toDateText(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function normalizeRecipients(recipients = []) {
  return (Array.isArray(recipients) ? recipients : [])
    .map((item) => ({
      employeeCode: String(item?.employeeCode || item?.EmployeeCode || "").trim(),
      role: String(item?.role || item?.Role || "").trim(),
      email: String(item?.email || item?.Email || "").trim(),
    }))
    .filter((item) => item.employeeCode || item.role || item.email);
}

export function normalizeNotification(record = {}) {
  const metadataRaw = getValue(record, "MetadataJson", "metadataJson", "metadata");
  return {
    ...record,
    id: String(getValue(record, "ID", "id", "notificationId") || ""),
    ID: getValue(record, "ID", "id", "notificationId"),
    title: extractChoiceValue(getValue(record, "Title", "title")),
    Title: extractChoiceValue(getValue(record, "Title", "title")),
    message: extractChoiceValue(getValue(record, "Message", "message", "Description", "Body")),
    Message: extractChoiceValue(getValue(record, "Message", "message", "Description", "Body")),
    module: extractChoiceValue(getValue(record, "Module", "module")),
    Module: extractChoiceValue(getValue(record, "Module", "module")),
    route: extractChoiceValue(getValue(record, "Route", "route")),
    Route: extractChoiceValue(getValue(record, "Route", "route")),
    isRead: toBoolean(getValue(record, "IsRead", "isRead"), false),
    IsRead: toBoolean(getValue(record, "IsRead", "isRead"), false),
    createdOn: getValue(record, "CreatedOn", "createdOn", "Created", "created") || "",
    CreatedOn: getValue(record, "CreatedOn", "createdOn", "Created", "created") || "",
    priority: extractChoiceValue(getValue(record, "Priority", "priority")) || "normal",
    metadata: safeJsonParse(metadataRaw, metadataRaw || {}),
  };
}

export function extractNotificationsFromResponse(data = {}) {
  const normalized = normalizePowerAutomateResponse(data);
  let list = normalized?.notifications ?? normalized?.value ?? normalized?.body?.notifications ?? normalized?.body?.value ?? [];

  // Power Automate sometimes returns an array as a JSON string in Response body.
  list = safeJsonParse(list, list);
  if (list?.value && Array.isArray(list.value)) list = list.value;
  if (list?.notifications && Array.isArray(list.notifications)) list = list.notifications;
  if (!Array.isArray(list)) return [];
  return list.map(normalizeNotification);
}

async function callNotificationFlow(payload) {
  ensureNotificationFlowUrl();
  const response = await fetch(HR_NOTIFICATION_FLOW_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({  ...payload }),
  });
  const rawData = await parseJsonResponse(response);
  const data = normalizePowerAutomateResponse(rawData);
  if (!response.ok || data?.success === false) {
    throw new Error(data?.message || data?.error?.message || data?.rawText || "Notification flow request failed.");
  }
  return data;
}

export async function createNotification({ requestContext = {}, notification = {}, recipients = [] } = {}) {
  const cleanRecipients = normalizeRecipients(recipients);
  if (cleanRecipients.length === 0) throw new Error("At least one notification recipient is required.");
  return callNotificationFlow({ action: "create", requestContext, notification, recipients: cleanRecipients });
}

export async function loadNotifications({ employeeCode = "", role = "", module = "", includeRead = true, top = 20 } = {}) {
  const data = await callNotificationFlow({ action: "load", loadFilter: { employeeCode, role, module, includeRead, top } });
  return { ...data, notifications: extractNotificationsFromResponse(data) };
}

export async function getUnreadNotificationCount({ employeeCode = "", role = "", module = "" } = {}) {
  const data = await callNotificationFlow({ action: "unreadCount", loadFilter: { employeeCode, role, module } });
  return data;
}

export async function markNotificationRead(notificationId) {
  if (!notificationId) throw new Error("Notification ID is required.");
  return callNotificationFlow({ action: "markRead", notificationId: String(notificationId) });
}

export async function markAllNotificationsRead({ employeeCode = "", role = "", module = "" } = {}) {
  return callNotificationFlow({ action: "markAllRead", loadFilter: { employeeCode, role, module } });
}

export function buildLeaveApplyNotificationPayload({ formData = {}, applyResult = {}, actorRole = "Employee", recipients = DEFAULT_APPROVER_NOTIFICATION_RECIPIENTS } = {}) {
  const employeeCode = String(formData.employeeCode || "").trim();
  const leaveType = formData.leaveType || "Leave";
  const fromText = toDateText(formData.fromDate);
  const toText = toDateText(formData.toDate);
  const referenceId = String(applyResult?.leaveRequestId || applyResult?.id || applyResult?.ID || "");
  return {
    requestContext: { actorEmployeeCode: employeeCode, actorName: formData.employeeName || "", actorRole, source: "LeaveManagement.js" },
    notification: {
      module: "LeaveManagement",
      section: "ApplyLeave",
      eventType: "leave.apply",
      referenceType: "LeaveRequest",
      referenceId,
      title: `Leave Request - ${leaveType}`,
      message: `${employeeCode} applied ${leaveType} from ${fromText} to ${toText}. Approval is pending with HR/Manager.`,
      priority: "normal",
      route: referenceId ? `/hr/leave?requestId=${referenceId}` : "/hr/leave",
      metadata: { leaveType, fromDate: formData.fromDate || "", toDate: formData.toDate || "", remarks: formData.reason || "" },
    },
    recipients,
  };
}

export function buildLeaveStatusNotificationPayload({ request = {}, action = "", remarks = "", actorEmployeeCode = "", actorRole = "Manager" } = {}) {
  const employeeCode = String(getValue(request, "employeeCode", "EmployeeCode") || "").trim();
  const employeeEmail = String(getValue(request, "employeeEmail", "EmployeeEmail", "email", "Email") || "").trim();
  const leaveType = getValue(request, "leaveType", "LeaveType") || "Leave";
  const referenceId = String(getValue(request, "id", "ID") || "");
  const fromDate = getValue(request, "fromDate", "FromDate") || "";
  const toDate = getValue(request, "toDate", "ToDate") || "";
  const fromText = toDateText(fromDate);
  const toText = toDateText(toDate);

  const config = {
    approve: { section: "LeaveApproval", eventType: "leave.approve", title: `Leave Approved - ${leaveType}`, message: `Your ${leaveType} from ${fromText} to ${toText} has been approved.`, priority: "normal" },
    reject: { section: "LeaveApproval", eventType: "leave.reject", title: `Leave Rejected - ${leaveType}`, message: `Your ${leaveType} request has been rejected. Reason: ${remarks || "Not specified"}.`, priority: "high" },
    approveCancellation: { section: "LeaveCancellation", eventType: "leave.cancel.approve", title: `Leave Cancellation Approved - ${leaveType}`, message: `Your leave cancellation request for ${leaveType} has been approved.`, priority: "normal" },
    rejectCancellation: { section: "LeaveCancellation", eventType: "leave.cancel.reject", title: `Leave Cancellation Rejected - ${leaveType}`, message: `Your leave cancellation request for ${leaveType} has been rejected. Reason: ${remarks || "Not specified"}.`, priority: "high" },
    cancelPending: { section: "LeaveCancellation", eventType: "leave.cancel.pending", title: `Pending Leave Cancelled - ${leaveType}`, message: `Your pending ${leaveType} request from ${fromText} to ${toText} has been cancelled.`, priority: "normal" },
    requestCancellation: { section: "LeaveCancellation", eventType: "leave.cancel.request", title: `Leave Cancellation Requested - ${leaveType}`, message: `${employeeCode} requested cancellation for ${leaveType}. Approval is pending with HR/Manager.`, priority: "normal" },
  };

  const selected = config[action] || { section: "LeaveManagement", eventType: `leave.${action || "update"}`, title: `Leave Update - ${leaveType}`, message: `Leave request updated for ${leaveType}.`, priority: "normal" };
  const employeeRecipient = { employeeCode, role: "Employee", email: employeeEmail };
  const recipients = action === "requestCancellation" ? DEFAULT_APPROVER_NOTIFICATION_RECIPIENTS : [employeeRecipient];

  return {
    requestContext: { actorEmployeeCode, actorRole, source: "LeaveManagement.js" },
    notification: {
      module: "LeaveManagement",
      section: selected.section,
      eventType: selected.eventType,
      referenceType: "LeaveRequest",
      referenceId,
      title: selected.title,
      message: selected.message,
      priority: selected.priority,
      route: referenceId ? `/hr/leave?requestId=${referenceId}` : "/hr/leave",
      metadata: { leaveType, fromDate, toDate, remarks: remarks || "" },
    },
    recipients,
  };
}

export default {
  createNotification,
  loadNotifications,
  getUnreadNotificationCount,
  markNotificationRead,
  markAllNotificationsRead,
  normalizeNotification,
  extractNotificationsFromResponse,
  buildLeaveApplyNotificationPayload,
  buildLeaveStatusNotificationPayload,
};
